import type { CrowdexSavedAgent } from '@crowdex/agent-contracts';
import { Button } from '@crowdex/ui/components/button';
import { InlineMessage } from '@crowdex/ui/components/feedback';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { AgentProfileViewer } from '@/components/agent-profile-viewer';
import { openNewConversation } from '@/features/apps/app-conversation';
// A persona's mark is shared with the projects and @ mentions.
import { PersonaMark } from '@/features/assistants/persona-mark';
import { agentDisplayName } from '@/features/workspaces/workspace-resources';
import type { useAccountAgentProfiles } from '@/hooks/use-account-agent-profiles';
import { SettingLinkRow, SettingsGroup, settingButtonClass } from './setting-controls';

/** What the new conversation asks the agent when the user makes a persona. */
export const NEW_PERSONA_REQUEST =
  'Help me create a persona: ask me its name, what it should do, which agent it runs on and what it may use, then save it as an agent profile of my account with the `assistant` tool `save_agent`.';

/** "New persona": a new conversation where the agent makes one with the user. */
export function startNewPersona(): void {
  openNewConversation(NEW_PERSONA_REQUEST);
}

/**
 * Settings › Personas: the account's saved agents (`/api/agent-profiles`), each opening its
 * page, and "New persona", which makes one with the agent in a new conversation.
 */
export function PersonasGroup({
  profiles,
  error,
  onOpen,
  onNew = startNewPersona,
}: {
  profiles: readonly CrowdexSavedAgent[];
  error: string | null;
  onOpen: (profile: CrowdexSavedAgent) => void;
  onNew?: () => void;
}) {
  return (
    <SettingsGroup
      aside={
        <button className={settingButtonClass('primary')} onClick={onNew} type="button">
          <Plus />
          New persona
        </button>
      }
      title={`Personas · ${profiles.length}`}
    >
      {error !== null && <p className="px-4 py-3 text-destructive text-sm">{error}</p>}
      {profiles.map((profile, index) => (
        <SettingLinkRow
          description={profile.description ?? `Runs on ${agentDisplayName(profile.agentId)}`}
          key={profile.id}
          leading={
            <PersonaMark
              index={index}
              persona={profile}
              shown={profiles.map((entry) => entry.name)}
            />
          }
          onSelect={() => onOpen(profile)}
          title={profile.name}
        />
      ))}
      {profiles.length === 0 && error === null && (
        <p className="px-4 py-3.5 text-muted-foreground text-sm">
          No persona yet. New persona opens a conversation where the agent makes one with you: a
          name, its instructions and what it may use.
        </p>
      )}
    </SettingsGroup>
  );
}

/** One persona's page, read-only, with Delete. */
export function PersonaSettingsView({
  profileId,
  profiles,
  replace,
  onBack,
}: {
  profileId: string;
  profiles: readonly CrowdexSavedAgent[];
  replace: ReturnType<typeof useAccountAgentProfiles>['replace'];
  onBack: () => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const profile = profiles.find((candidate) => candidate.id === profileId);
  if (profile === undefined)
    return (
      <div className="space-y-4">
        <p className="text-muted-foreground text-sm">This persona is not in your account.</p>
        <Button onClick={onBack} size="sm" variant="outline">
          Back to personas
        </Button>
      </div>
    );
  return (
    <div className="-mx-8 -mt-10">
      {error && (
        <InlineMessage className="m-4" tone="error">
          {error}
        </InlineMessage>
      )}
      <AgentProfileViewer
        actions={[
          {
            id: 'delete',
            label: 'Delete',
            run: () => {
              if (!window.confirm(`Delete “${profile.name}”?`)) return;
              void replace(profiles.filter((candidate) => candidate.id !== profile.id)).then(
                onBack,
                (reason: unknown) =>
                  setError(`Could not delete “${profile.name}”: ${String(reason)}`),
              );
            },
          },
        ]}
        backLabel="Back to personas"
        onBack={onBack}
        profile={profile}
      />
    </div>
  );
}
